import React, {useEffect, useMemo, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {Modal} from "../../../../components/modal/modal.jsx";
import Button from "../../../../components/button/button.jsx";
import InputForm from "../../../../components/input/input-form.jsx";
import InputTextarea from "../../../../components/input/input-textarea.jsx";
import SelectComponent from "../../../../components/select/select-component.jsx";
import Loader from "../../../../components/template/loader.jsx";
import GarrisonService from "../../../../services/dispatch/garrison-service.js";
import projectUtils from "../../../../utils/project-utils.js";
import {DASHBOARD_QUERY_KEYS} from "../utils/dashboard-query-keys.js";
import {useDashboardContext} from "../context/dashboard-context.js";
import useDashboardModalState from "../hooks/use-dashboard-modal-state.js";
import {FaCircleExclamation, FaTriangleExclamation} from "react-icons/fa6";

const ChangeContingentsModal = ({payload, onClose, onSuccess}) => {
    const {configuration} = useDashboardContext();
    const [rows, setRows] = useState([]);
    const [animals, setAnimals] = useState([]);
    const [finalObservation, setFinalObservation] = useState("");
    const [schedulesJustification, setSchedulesJustification] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const requested = Boolean(payload);
    const garrison = payload?.garrison;
    const garrisonId = garrison?.garrisonId;

    const changeQuery = useQuery({
        queryKey: DASHBOARD_QUERY_KEYS.changeContingents(garrisonId),
        queryFn: () => GarrisonService.getChangeContingentsGarrisonByDash(garrisonId),
        enabled: requested && Boolean(garrisonId),
    });
    const isReady = !changeQuery.isFetching && (changeQuery.isSuccess || changeQuery.isError);
    const {open, closeModal, isPreparing} = useDashboardModalState({payload, isReady, onClose});

    const result = changeQuery.data ?? {};
    const contingentOptions = useMemo(() => (result.contingent ?? []).map(item => ({
        value: item.id,
        label: item.namewar,
        cnhWarning: item.cnhWarning,
        cnhWarningText: item.cnhWarningText,
    })), [result.contingent]);
    const animalOptions = useMemo(() => (result.animals ?? []).map(item => ({value: item.id, label: item.name})), [result.animals]);
    const selectedContingentIds = useMemo(() => rows
        .flatMap(row => row.positions.map(position => position.contingentId))
        .filter(Boolean)
        .map(String), [rows]);

    useEffect(() => {
        if (!requested) return;
        setFinalObservation("");
        setSchedulesJustification("");
        setIsSubmitting(false);
    }, [requested]);

    useEffect(() => {
        const nextRows = (result.garrisonComposition ?? []).map(garrisonComposition => {
            const composition = (result.compositions ?? []).find(item => item.id === garrisonComposition.compositionId);
            return {
                compositionId: garrisonComposition.compositionId,
                garrisonCompositionId: garrisonComposition.id,
                name: garrisonComposition.name,
                kmControl: Boolean(composition?.vehicleModel?.kmControl),
                finalMileage: configuration.isSuggestKm && composition?.vehicleModel?.kmControl && composition.vehicleModel.actualKm !== undefined && composition.vehicleModel.actualKm !== null
                    ? String(composition.vehicleModel.actualKm)
                    : "",
                mileageWarning: "",
                allowInvalidMileage: false,
                positions: (composition?.positions ?? []).map(position => ({
                    name: position.name,
                    conductor: position.conductor,
                    contingentId: garrisonComposition.integrants?.find(integrant => integrant.position === position.name)?.contingent?.id ?? "",
                })),
            };
        });
        setRows(nextRows);
        setAnimals((result.garrisonAnimals ?? []).map(animal => animal.id));
    }, [configuration.isSuggestKm, result.compositions, result.garrisonAnimals, result.garrisonComposition]);

    const updatePosition = (compositionId, positionName, contingentId) =>
        setRows(prev => prev.map(row => row.compositionId === compositionId
            ? {...row, positions: row.positions.map(position => position.name === positionName ? {...position, contingentId} : position)}
            : row));

    const updateMileage = (compositionId, finalMileage) =>
        setRows(prev => prev.map(row => row.compositionId === compositionId ? {...row, finalMileage, mileageWarning: "", allowInvalidMileage: false} : row));

    const setAllowInvalidMileage = (compositionId, value) =>
        setRows(prev => prev.map(row => row.compositionId === compositionId ? {...row, allowInvalidMileage: value} : row));

    const getPositionContingentOptions = currentContingentId => contingentOptions.map(option => ({
        ...option,
        isDisabled: selectedContingentIds.includes(String(option.value)) && String(option.value) !== String(currentContingentId),
    }));

    const getCnhWarning = contingentId => {
        if (!contingentId) return null;
        const option = contingentOptions.find(item => String(item.value) === String(contingentId));
        if (!option?.cnhWarning || !option?.cnhWarningText) return null;
        return {
            status: option.cnhWarning === "danger" ? "danger" : "warning",
            text: option.cnhWarningText,
        };
    };

    const validateMileage = async () => {
        const warnings = {};
        let isValid = true;

        for (const row of rows) {
            if (!row.kmControl || row.finalMileage === "") continue;

            const result = await GarrisonService.checkKmByCompositionCloseGarrison(row.garrisonCompositionId, row.finalMileage);
            const warning = result?.invalid ? String(result.message ?? "").replaceAll("<br/>", "\n") : "";

            warnings[row.compositionId] = warning;
            if (warning && !row.allowInvalidMileage) isValid = false;
        }

        setRows(prev => prev.map(row => ({...row, mileageWarning: warnings[row.compositionId] ?? ""})));
        return isValid;
    };

    const handleSubmit = async () => {
        if (result.justifySchedules && !schedulesJustification) {
            projectUtils.swalErrorMessage("", "A justificativa é obrigatória.", "Ok");
            return;
        }

        setIsSubmitting(true);

        try {
            const isMileageValid = await validateMileage();
            if (!isMileageValid) {
                setIsSubmitting(false);
                return;
            }

            await GarrisonService.setChangeContingents({
                id: garrisonId,
                finalObservation,
                schedulesJustification,
                animals: animals.filter(Boolean).map(Number),
                compositions: rows.map(row => ({
                    compositionId: row.compositionId,
                    finalMileage: row.finalMileage ? Number(row.finalMileage) : null,
                    integrants: row.positions
                        .filter(position => position.contingentId)
                        .map(position => ({contingentId: Number(position.contingentId), position: position.name})),
                })),
            });

            onSuccess();
            projectUtils.swalSuccessMessage("success", "Sucesso", "Troca de contingentes realizada com sucesso.", "Ok");
        } catch (error) {
            console.error("Erro ao alterar contingentes pelo dashboard:", error);
            const message = error.response?.data ?? "Não foi possível realizar a troca de contingentes";
            projectUtils.swalErrorMessage("", message, "Ok");
            setIsSubmitting(false);
        }
    };

    const getCnhWarningIcon = (status) => {
        if (!status) return null;

        switch (status) {
            case "danger":
                return <FaTriangleExclamation className="text-red-500" />;
            case "warning":
                return <FaCircleExclamation className="text-amber-500" />;
            default:
                return null;
        }
    };

    return (
        <>
            {isPreparing && <Loader/>}
            <Modal open={open} setOpen={closeModal} title="Alterar contingentes" className="w-full max-w-5xl" isCloseClick={false} animated={false}>
            <Modal.Body className="relative flex max-h-[calc(100vh-180px)] flex-col gap-4 overflow-auto">
                {changeQuery.isError && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">Não foi possível carregar os dados da guarnição.</div>}
                {!changeQuery.isError && (
                    <>
                        <div><p className="text-[14px] font-bold text-[#676a6c]">Guarnição</p><span className="text-sm text-[#555]">{result.garrisonName ?? garrison?.garrisonName}</span></div>
                        {rows.map(row => (
                            <div key={row.compositionId} className="border-t border-[#ddd] pt-3">
                                <p className="text-[14px] font-bold text-[#676a6c]">Composição</p>
                                <span className="text-sm text-[#555]">{row.name}</span>
                                <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                                    {row.positions.map(position => {
                                        const cnhWarning = getCnhWarning(position.contingentId);

                                        return (
                                            <div key={`${row.compositionId}-${position.name}`}>
                                                <SelectComponent
                                                    classNameWrapper="w-full"
                                                    label={position.name}
                                                    value={position.contingentId}
                                                    options={getPositionContingentOptions(position.contingentId)}
                                                    onChange={selected => updatePosition(row.compositionId, position.name, selected?.value ?? "")}
                                                    isClearable={true}
                                                    helperText={position.conductor ? cnhWarning?.text : ""}
                                                    status={position.conductor ? (cnhWarning?.status === "danger" ? "danger" : "warning") : ""}
                                                    customIcon={position.conductor ? getCnhWarningIcon(cnhWarning?.status) : ""}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                                {row.kmControl && <InputForm classNameWrapper={"mt-3"} label="Km final" integerOnly type="number" value={row.finalMileage} min={1} onChange={({target}) => updateMileage(row.compositionId, target.value)}/>}
                                {row.mileageWarning && (
                                    <div className="mt-2 rounded border border-[#f8ac59] bg-[#fff7ed] p-2 text-[12px] text-[#8a5a20]">
                                        <p className="whitespace-pre-line">{row.mileageWarning}</p>
                                        <label className="mt-2 flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                checked={row.allowInvalidMileage}
                                                onChange={({target}) => setAllowInvalidMileage(row.compositionId, target.checked)}
                                            />
                                            <span>Salvar mesmo assim</span>
                                        </label>
                                    </div>
                                )}
                            </div>
                        ))}
                        {animals.map((animalId, index) => (
                            <SelectComponent
                                key={index}
                                classNameWrapper="w-full"
                                label="Animal"
                                value={animalId}
                                options={animalOptions}
                                onChange={selected => setAnimals(prev => prev.map((item, itemIndex) => itemIndex === index ? selected?.value ?? "" : item))}
                            />
                        ))}
                        <InputTextarea label="Observação final" value={finalObservation} onChange={({target}) => setFinalObservation(target.value)} height={120}/>
                        {result.justifySchedules && <InputTextarea label="Justificativa de atividades não realizadas *" value={schedulesJustification} onChange={({target}) => setSchedulesJustification(target.value)} height={120}/>}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" onClick={handleSubmit} loading={isSubmitting} disabled={changeQuery.isError || changeQuery.isLoading} actionFeedbackType='save'>Salvar</Button>
            </Modal.Footer>
            </Modal>
        </>
    );
};

export default ChangeContingentsModal;